import type { FilterOption } from '@odo/shared-ui';
import type { StatusMeta } from '../../../../services/workQueue';
import type { StatusCatalog } from '../../../../services/statusCatalog';

export interface PhaseStepGroup {
    phase: string;
    steps: FilterOption[];
}

export function buildPhaseOptions(phases: readonly string[]): FilterOption[] {
    return phases.map((phase) => ({ value: phase, label: phase }));
}

// Only live catalog steps are offered (deprecated ones stay reachable through the phase
// filter); ordered by StatusMeta.order so a search for "9.1" reads in engine order.
export function groupStatusesByPhase(
    statuses: readonly StatusMeta[],
    phases: readonly string[],
    catalog: StatusCatalog
): PhaseStepGroup[] {
    return phases
        .map((phase) => ({
            phase,
            steps: statuses
                .filter((status) => status.phase === phase && !status.deprecated)
                .sort((a, b) => a.order - b.order)
                .map((status) => ({ value: status.id, label: catalog.display(status.id) }))
        }))
        .filter((group) => group.steps.length > 0);
}

// Never a real internal_case_status value, so an `IN` filter on it matches
// nothing - the honest way to express "these two pills asked for zero rows"
// on the wire, rather than silently dropping one of them.
const NO_MATCH_SENTINEL = '__no_internal_status_match__';

/**
 * Combines the Phase and Internal step pills into the one `internalStatus`
 * filter the server accepts. Both active at once intersects, it doesn't
 * union: picking phase "Retention" and a step that only lives under "Init"
 * asked for a status that is neither, and an empty intersection is sent as
 * the sentinel above rather than falling back to either pill alone.
 */
export function resolveInternalStatusFilter(
    selectedPhases: readonly string[],
    selectedSteps: readonly string[],
    statuses: readonly StatusMeta[]
): string[] | undefined {
    if (selectedPhases.length === 0 && selectedSteps.length === 0) {
        return undefined;
    }
    if (selectedPhases.length === 0) {
        return [...selectedSteps];
    }
    const phaseStatusIds = statuses
        .filter((status) => selectedPhases.includes(status.phase))
        .map((status) => status.id);
    if (selectedSteps.length === 0) {
        return phaseStatusIds;
    }
    const stepSet = new Set(selectedSteps);
    const intersection = phaseStatusIds.filter((id) => stepSet.has(id));
    return intersection.length > 0 ? intersection : [NO_MATCH_SENTINEL];
}
